/**
 * "Nest when idle" — Koko flies to a corner and perches after N
 * minutes of no user activity. Mirrors `KokoNest.swift`.
 *
 * Parameters ported verbatim:
 *   - idle threshold default 3 min (settings: `nestIdleMinutes`)
 *   - stepper range 1–30 min
 *   - corner inset 60 pt
 *   - picks the corner furthest from the current cursor so it isn't
 *     in the user's way
 *   - transitions to `perched` on arrival
 *
 * Reset triggers (from `CompanionManager.swift:1301, 1495`):
 *   - mousemove
 *   - user submits a typed or voice turn
 *   - wake word detected
 *   - hotkey pressed
 *
 * On wake-up the idle timer simply starts again — the bird's existing
 * cursor-follow spring will yank it back toward the cursor naturally.
 */

import type { SettingsStore } from "../app/SettingsStore";
import type { MovementController } from "./MovementController";

const CORNER_INSET_PX = 60;

export interface NestControllerOptions {
  settings: SettingsStore;
  movementController: MovementController;
  getLastCursorPosition: () => { x: number; y: number };
  /**
   * Optional host-supplied perch targets in viewport coordinates. When
   * this returns a non-empty list (e.g. the studio's procedural branch
   * tips), the bird perches on the target furthest from the cursor
   * instead of a bare screen corner. Returns null/empty → corner
   * nesting, exactly as before. Re-queried on every nest so the host
   * can reflect layout changes (resize, scroll).
   */
  getPerchPoints?: () =>
    | { viewportX: number; viewportY: number }[]
    | null
    | undefined;
}

export class NestController {
  private readonly options: NestControllerOptions;
  private timerId: number | null = null;
  private isRunning = false;
  private isCurrentlyNested = false;
  private currentNestTarget: { viewportX: number; viewportY: number } | null = null;

  constructor(options: NestControllerOptions) {
    this.options = options;

    // React to user changes — if they flip the toggle off mid-idle,
    // we stop the timer immediately so the bird doesn't surprise them.
    options.settings.subscribe("nestEnabled", () => this.rearm());
    options.settings.subscribe("nestIdleMinutes", () => this.rearm());
    options.settings.subscribe("sessionActive", () => this.rearm());
  }

  start(): void {
    this.isRunning = true;
    this.rearm();
  }

  stop(): void {
    this.isRunning = false;
    this.clearTimer();
  }

  /**
   * Call from any user activity — cursor motion, typed input, push-to-
   * talk, wake word, hotkey. Wakes the bird if it's nested AND restarts
   * the idle countdown so it doesn't perch again immediately. Matches
   * native `CompanionManager.swift:1301, 1495` behavior exactly.
   */
  resetIdleTimer(): void {
    if (!this.isRunning) return;
    if (this.isCurrentlyNested) {
      this.wakeFromNest();
    }
    this.rearm();
  }

  /** Whether the bird is currently perched at a nest corner. */
  isNested(): boolean {
    return this.isCurrentlyNested;
  }

  /**
   * Returns the active nest corner position, or null if the bird is
   * not currently nested. Pointing flights use this to remember where
   * to fly back to after the 3.5s hold completes — without it the bird
   * would drift back to the cursor and the nest state would silently
   * break, mirroring the bug in `runProactiveObservation` before this
   * fix landed.
   */
  getCurrentNestTarget(): { viewportX: number; viewportY: number } | null {
    return this.isCurrentlyNested ? this.currentNestTarget : null;
  }

  /**
   * Fly back to the saved nest corner without resetting the idle
   * countdown. Used by `KokoApp.runPointing` after the bird has flown
   * out from the nest to point at a target — once the hold completes
   * the bird should re-perch at the same corner instead of drifting
   * back to the cursor.
   */
  async returnToNestTarget(
    target: { viewportX: number; viewportY: number },
  ): Promise<void> {
    this.isCurrentlyNested = true;
    this.currentNestTarget = target;
    await this.options.movementController.flyToNestTarget(target);
  }

  private rearm(): void {
    this.clearTimer();
    if (!this.isRunning) return;
    if (!this.options.settings.get("sessionActive")) return;
    if (!this.options.settings.get("nestEnabled")) return;

    const idleMinutes = Math.max(
      0.02, // test-only floor so suites can force nesting in ~1s
      this.options.settings.get("nestIdleMinutes"),
    );
    const idleMs = idleMinutes * 60 * 1000;
    this.timerId = window.setTimeout(() => {
      this.timerId = null;
      void this.nestNow();
    }, idleMs);
  }

  private async nestNow(): Promise<void> {
    if (this.isCurrentlyNested) return;
    const target = this.pickNestTarget();
    this.isCurrentlyNested = true;
    this.currentNestTarget = target;
    await this.options.movementController.flyToNestTarget(target);
  }

  /**
   * Choose where to perch. Host-supplied perch points (branch tips) win
   * when present; otherwise fall back to the furthest screen corner. In
   * both cases we pick the target furthest from the cursor so the bird
   * settles out of the user's way.
   */
  private pickNestTarget(): { viewportX: number; viewportY: number } {
    const perchPoints = this.options.getPerchPoints?.() ?? null;
    if (perchPoints && perchPoints.length > 0) {
      return this.pickFurthestFromCursor(perchPoints);
    }
    return this.pickCornerFurthestFromCursor();
  }

  private pickFurthestFromCursor<T extends { viewportX: number; viewportY: number }>(
    points: T[],
  ): T {
    const { x: cursorX, y: cursorY } = this.options.getLastCursorPosition();
    let furthest = points[0];
    let furthestDistance = -1;
    for (const point of points) {
      const dx = point.viewportX - cursorX;
      const dy = point.viewportY - cursorY;
      const distance = dx * dx + dy * dy;
      if (distance > furthestDistance) {
        furthestDistance = distance;
        furthest = point;
      }
    }
    return furthest;
  }

  private wakeFromNest(): void {
    this.isCurrentlyNested = false;
    this.currentNestTarget = null;
    this.options.movementController.leaveNest();
  }

  private clearTimer(): void {
    if (this.timerId !== null) {
      window.clearTimeout(this.timerId);
      this.timerId = null;
    }
  }

  private pickCornerFurthestFromCursor(): { viewportX: number; viewportY: number } {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    return this.pickFurthestFromCursor([
      { viewportX: CORNER_INSET_PX, viewportY: CORNER_INSET_PX },
      { viewportX: viewportWidth - CORNER_INSET_PX, viewportY: CORNER_INSET_PX },
      { viewportX: CORNER_INSET_PX, viewportY: viewportHeight - CORNER_INSET_PX },
      {
        viewportX: viewportWidth - CORNER_INSET_PX,
        viewportY: viewportHeight - CORNER_INSET_PX,
      },
    ]);
  }
}
